import { Injectable, inject } from "@angular/core";
import { Observable } from "rxjs";

import { ApiService } from "@core/services";
import { ApiResponse, FavouriteReport, Report, ReportExecuteRequest, ReportExecuteResponse, ReportParameterControl, ReportParameterDefaults } from "@core/types";

const REPORT_API = {
  USER_REPORTS: "api/report/user-report",
  RECENT_REPORTS: "api/report/recent-reports",
  FAVORITE_REPORTS: "api/report/favorite-reports",
  UPDATE_RECENT_REPORT: "api/report/update-recent-report",
  UPDATE_FAVORITES: "api/report/update-favorite-reports",
  DELETE_FAVORITES: "api/report/delete-favorite-reports",
  SET_PARAMETERS: "api/report/set-report-parameters",
  GET_PARAMETERS: "api/report/get-report-parameters",
  RUN_REPORT: "api/report/run",
  VALIDATE_PARAMETERS: "api/report/validate-parameters",
  REPORT_DEFAULTS: "api/report/report-parameter-defaults",
  CONTROLS: "api/report/report-parameter-controls",
};

@Injectable({ providedIn: "root" })
export class ReportService {
  private readonly api = inject(ApiService);

  /**
   * Returns available reports for the current user.
   */
  getUserReports(): Observable<ApiResponse<Report[]>> {
    return this.api.get<Report[]>(REPORT_API.USER_REPORTS);
  }

  getReportParameterDefaults(reportId: number): Observable<ApiResponse<ReportParameterDefaults[]>> {
    return this.api.get<ReportParameterDefaults[]>(REPORT_API.REPORT_DEFAULTS, { params: { reportId } });
  }

  getReportParameterControls(reportId: number): Observable<ApiResponse<ReportParameterControl[]>> {
    return this.api.get<ReportParameterControl[]>(REPORT_API.CONTROLS, { params: { reportId } });
  }

  /**
   * Returns recently viewed reports for the current user.
   */
  getRecentReports(): Observable<ApiResponse<Report[]>> {
    return this.api.get<Report[]>(REPORT_API.RECENT_REPORTS);
  }

  /**
   * Returns favorited reports for the current user.
   */
  getFavoriteReports(): Observable<ApiResponse<FavouriteReport[]>> {
    return this.api.get<FavouriteReport[]>(REPORT_API.FAVORITE_REPORTS);
  }

  /**
   * Records that the user viewed a report (updates recent history).
   */
  updateRecentReport(reportId: number): Observable<ApiResponse<void>> {
    return this.api.get<void>(REPORT_API.UPDATE_RECENT_REPORT, { params: { reportId } });
  }

  /**
   * Adds or reorders a report in the user's favorites.
   */
  updateFavoriteReports(reportId: number, sortReports?: string): Observable<ApiResponse<void>> {
    const params: Record<string, string | number> = { reportid: reportId };
    if (sortReports) params.sortReports = sortReports;
    return this.api.get<void>(REPORT_API.UPDATE_FAVORITES, { params });
  }

  /**
   * Removes a report from the user's favorites.
   */
  deleteFavoriteReport(reportId: number): Observable<ApiResponse<void>> {
    return this.api.get<void>(REPORT_API.DELETE_FAVORITES, { params: { reportID: reportId } });
  }

  // --------------------
  // Report Parameters
  // --------------------

  /**
   * Caches report parameters server-side for secure handoff to the viewer.
   */
  saveReportParameters(paramsId: string, payload: ReportExecuteRequest): Observable<ApiResponse<void>> {
    return this.api.post<void>(REPORT_API.SET_PARAMETERS, payload, { params: { paramsId } });
  }

  /**
   * Retrieves cached report parameters by session key.
   */
  getReportParameters(paramsId: string): Observable<ApiResponse<ReportExecuteRequest>> {
    return this.api.get<ReportExecuteRequest>(REPORT_API.GET_PARAMETERS, { params: { paramsId } });
  }

  /**
   * Executes a report and returns the raw payload (HTML or base64-encoded binary).
   */
  runReport(payload: ReportExecuteRequest): Observable<ApiResponse<ReportExecuteResponse>> {
    return this.api.post<ReportExecuteResponse>(REPORT_API.RUN_REPORT, payload);
  }

  /**
   * Validates report parameters without executing the report, returning any validation errors.
   */
  validateParameters(payload: ReportExecuteRequest): Observable<ApiResponse<string[]>> {
    return this.api.post<string[]>(REPORT_API.VALIDATE_PARAMETERS, payload);
  }
}
